import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { AiChatMessageItem } from '@app/core/services/ai-chat.types';
import { sanitizeAiChatPromptHtml } from '@app/core/services/ai-chat.utils';

type AiChatFrameKind = 'prompt' | 'reasoning' | 'response';

interface AiChatFrameState {
    source: string;
    ready: boolean;
}

@Component({
    selector: 'tt-ai-chat-message',
    templateUrl: './ai-chat-message.component.html',
    styleUrls: ['./ai-chat-message.component.css'],
    standalone: false,
})
export class AiChatMessageComponent implements OnDestroy, OnInit {
    private readonly trustedHtml = new Map<string, { source: string; value: SafeHtml }>();
    private readonly trustedMarkdown = new Map<string, { source: string; value: SafeHtml }>();
    private readonly frameObservers = new Map<HTMLIFrameElement, ResizeObserver>();
    private readonly frameStates = new Map<AiChatFrameKind, AiChatFrameState>();
    private themeObserver?: MutationObserver;
    private reasoningAnimationFrame?: number;
    private latestReasoningSummary = '';
    private renderedReasoningSummary = '';

    @Input()
    public set message(message: AiChatMessageItem) {
        this.messageItem = message;
        this.updateReasoningSummary(message.reasoning_status ?? '');
    }

    public get message(): AiChatMessageItem {
        return this.messageItem;
    }

    private messageItem!: AiChatMessageItem;

    @Input()
    public messageIndex = 0;

    @ViewChild('reasoningFrame')
    private reasoningFrame?: ElementRef<HTMLIFrameElement>;

    /** The iframe source is retained during a continuation animation to avoid a reload flash. */
    public reasoningMarkdownSource = '';

    @Output()
    public readonly contentResized = new EventEmitter<void>();

    constructor(private sanitizer: DomSanitizer) {
    }

    public getTrustedHtml(type: 'prompt' | 'response', html: string): SafeHtml {
        const key = `${this.message.ai_message_keyno}:${type}`;
        const cached = this.trustedHtml.get(key);
        const source = type === 'prompt' ? sanitizeAiChatPromptHtml(html) : html;
        if (cached?.source === source) return cached.value;

        const value = this.sanitizer.bypassSecurityTrustHtml(source);
        this.trustedHtml.set(key, { source, value });
        return value;
    }

    public getTrustedMarkdown(markdown: string): SafeHtml {
        const cached = this.trustedMarkdown.get(this.message.ai_message_keyno);
        if (cached?.source === markdown) return cached.value;

        const value = this.sanitizer.bypassSecurityTrustHtml(this.getSafeMarkdownHtml(markdown));
        this.trustedMarkdown.set(this.message.ai_message_keyno, { source: markdown, value });
        return value;
    }

    /** Returns whether the frame has loaded, been themed, and received its measured height. */
    public isFrameReady(type: AiChatFrameKind, source: string): boolean {
        const state = this.frameStates.get(type);

        if (!state || state.source !== source) {
            this.frameStates.set(type, { source, ready: false });
            return false;
        }

        return state.ready;
    }

    public resizeFrame(event: Event, type: AiChatFrameKind): void {
        const frame = event.target as HTMLIFrameElement;
        const frameDocument = frame.contentDocument;
        if (!frameDocument?.body) return;

        this.removeDisconnectedFrameObservers();
        this.applyApplicationStyles(frame, frameDocument);
        this.frameObservers.get(frame)?.disconnect();

        const resize = () => {
            frame.style.height = `${Math.max(frameDocument.documentElement.scrollHeight, frameDocument.body.scrollHeight)}px`;
            this.contentResized.emit();
        };
        frame.style.height = '0';
        resize();
        this.frameStates.set(type, { source: this.frameStates.get(type)?.source ?? '', ready: true });

        const observer = new ResizeObserver(resize);
        observer.observe(frameDocument.body);
        frameDocument.addEventListener('load', resize, true);
        this.frameObservers.set(frame, observer);
    }

    private applyApplicationStyles(frame: HTMLIFrameElement, frameDocument: Document) {
        const frameStyles = getComputedStyle(frame);
        const applicationStyles = getComputedStyle(document.documentElement);
        const question = frame.closest('.tt-ai-chat-message__message--question') !== null;
        const backgroundColor = (question ? applicationStyles.getPropertyValue('--tt-primary-color') : applicationStyles.getPropertyValue('--tt-body-color')).trim() || frameStyles.backgroundColor;
        const textColor = (question ? applicationStyles.getPropertyValue('--tt-primary-text-color') : applicationStyles.getPropertyValue('--tt-body-text-color')).trim() || frameStyles.color;
        const primaryColor = applicationStyles.getPropertyValue('--tt-primary-color').trim();
        const fontFamily = applicationStyles.getPropertyValue('--tt-body-font-family').trim() || frameStyles.fontFamily;
        const fontSize = applicationStyles.getPropertyValue('--tt-font-size').trim() || frameStyles.fontSize;
        const body = frameDocument.body;

        frame.style.backgroundColor = backgroundColor;
        frameDocument.documentElement.style.backgroundColor = backgroundColor;
        frameDocument.documentElement.style.color = textColor;
        body.style.backgroundColor = backgroundColor;
        body.style.color = textColor;
        body.style.fontFamily = fontFamily;
        body.style.fontSize = fontSize;
        body.style.lineHeight = '1.5';
        body.style.margin = '0';
        body.style.overflowWrap = 'anywhere';

        let styles = frameDocument.querySelector<HTMLStyleElement>('style[data-tt-ai-chat]');
        if (!styles) {
            styles = frameDocument.createElement('style');
            styles.dataset['ttAiChat'] = '';
            frameDocument.head.appendChild(styles);
        }

        styles.textContent = `
            :root { color-scheme: ${document.documentElement.classList.contains('dark') ? 'dark' : 'light'}; }
            html, body { background-color: ${backgroundColor} !important; color: ${textColor} !important; font-family: ${fontFamily} !important; font-size: ${fontSize} !important; overflow: hidden !important; }
            [data-tt-ai-chat-surface] { background: rgba(255, 255, 255, 0.08) !important; }
            [data-tt-ai-chat-text] { color: ${textColor} !important; }
            [data-tt-ai-chat-border] { border-color: rgba(255, 255, 255, 0.2) !important; }
            img, video { max-width: 100%; height: auto; }
            pre { overflow-x: auto; white-space: pre-wrap; }
            code { background-color: rgba(127, 127, 127, 0.16); border-radius: 0.25em; color: ${textColor} !important; padding: 0.1em 0.3em; }
            pre { background-color: rgba(127, 127, 127, 0.16); border-radius: 0.4em; color: ${textColor} !important; padding: 0.8em; }
            pre *, code * { color: ${textColor} !important; }
            pre code { background-color: transparent; padding: 0; }
            table { border-collapse: collapse; display: block; max-width: 100%; overflow-x: auto; width: max-content; }
            th, td { border: 1px solid rgba(127, 127, 127, 0.35); padding: 0.4em 0.6em; text-align: left; }
            blockquote { border-left: 0.25em solid rgba(127, 127, 127, 0.5); margin-left: 0; padding-left: 0.8em; }
            a { color: ${primaryColor}; }
            :first-child { margin-top: 0; }
            :last-child { margin-bottom: 0; }
        `;
        this.applyDarkThemeToContent(frameDocument, !question && document.documentElement.classList.contains('dark'));
    }

    /** Animates only provider summaries that extend the prior summary; replacement summaries render immediately. */
    private updateReasoningSummary(summary: string): void {
        const previousSummary = this.latestReasoningSummary;
        this.latestReasoningSummary = summary;
        this.cancelReasoningAnimation();

        if (!previousSummary || !summary.startsWith(previousSummary)) {
            this.renderedReasoningSummary = summary;
            this.reasoningMarkdownSource = summary;
            return;
        }

        const frame = this.reasoningFrame?.nativeElement;
        if (!frame?.contentDocument?.body) {
            this.renderedReasoningSummary = summary;
            this.reasoningMarkdownSource = summary;
            return;
        }

        const initialSummary = summary.startsWith(this.renderedReasoningSummary) ? this.renderedReasoningSummary : previousSummary;
        const suffix = summary.slice(initialSummary.length);

        if (!suffix) return;

        const duration = Math.min(600, Math.max(160, suffix.length * 10));
        let startedAt: number | undefined;

        const animate = (timestamp: number): void => {
            startedAt ??= timestamp;
            const progress = Math.min(1, (timestamp - startedAt) / duration);
            const length = Math.ceil(suffix.length * progress);
            this.renderedReasoningSummary = initialSummary + suffix.slice(0, length);
            this.renderReasoningFrame(frame, this.renderedReasoningSummary);

            if (progress < 1) {
                this.reasoningAnimationFrame = requestAnimationFrame(animate);
            } else {
                this.reasoningAnimationFrame = undefined;
            }
        };

        this.reasoningAnimationFrame = requestAnimationFrame(animate);
    }

    private renderReasoningFrame(frame: HTMLIFrameElement, markdown: string): void {
        const frameDocument = frame.contentDocument;
        if (!frameDocument?.body) return;

        frameDocument.body.innerHTML = this.getSafeMarkdownHtml(markdown);
        this.applyApplicationStyles(frame, frameDocument);
        frame.style.height = `${Math.max(frameDocument.documentElement.scrollHeight, frameDocument.body.scrollHeight)}px`;
        this.contentResized.emit();
    }

    private getSafeMarkdownHtml(markdown: string): string {
        const renderer = new marked.Renderer();
        renderer.html = () => '';
        const parsedMarkdown = marked.parse(markdown, { async: false, breaks: true, gfm: true, renderer });
        const html = DOMPurify.sanitize(parsedMarkdown, {
            FORBID_ATTR: ['style'],
            FORBID_TAGS: ['button', 'embed', 'form', 'iframe', 'input', 'object', 'script', 'style'],
            USE_PROFILES: { html: true },
        }).trim();
        return html || this.escapeHtml(markdown).replace(/\r?\n/g, '<br>');
    }

    private cancelReasoningAnimation(): void {
        if (this.reasoningAnimationFrame !== undefined) {
            cancelAnimationFrame(this.reasoningAnimationFrame);
            this.reasoningAnimationFrame = undefined;
        }
    }

    private removeDisconnectedFrameObservers() {
        this.frameObservers.forEach((observer, frame) => {
            if (frame.isConnected) return;

            observer.disconnect();
            this.frameObservers.delete(frame);
        });
    }

    private applyDarkThemeToContent(frameDocument: Document, darkMode: boolean) {
        const elements = Array.from(frameDocument.body.querySelectorAll<HTMLElement>('*'));
        elements.forEach((element) => {
            delete element.dataset['ttAiChatSurface'];
            delete element.dataset['ttAiChatText'];
            delete element.dataset['ttAiChatBorder'];
        });
        if (!darkMode) return;

        elements.forEach((element) => {
            if (['IMG', 'VIDEO', 'CANVAS', 'SVG', 'PATH', 'PICTURE'].includes(element.tagName)) return;
            const styles = frameDocument.defaultView?.getComputedStyle(element);
            if (!styles) return;
            if (this.isLightOpaqueColor(styles.backgroundColor) || this.hasLightBackgroundImage(styles.backgroundImage)) {
                element.dataset['ttAiChatSurface'] = '';
            }
            if (this.isNeutralDarkColor(styles.color)) element.dataset['ttAiChatText'] = '';
            if (this.isLightVisibleColor(styles.borderTopColor)) element.dataset['ttAiChatBorder'] = '';
        });
    }

    private isLightOpaqueColor(color: string) {
        const rgba = this.getRgba(color);
        return !!rgba && rgba.alpha > 0.9 && this.getLuminance(rgba) > 0.8;
    }

    private isNeutralDarkColor(color: string) {
        const rgba = this.getRgba(color);
        if (!rgba || rgba.alpha < 0.5 || this.getLuminance(rgba) > 0.55) return false;
        return Math.max(rgba.red, rgba.green, rgba.blue) - Math.min(rgba.red, rgba.green, rgba.blue) < 36;
    }

    private isLightVisibleColor(color: string) {
        const rgba = this.getRgba(color);
        return !!rgba && rgba.alpha > 0.1 && this.getLuminance(rgba) > 0.7;
    }

    private hasLightBackgroundImage(backgroundImage: string) {
        if (!backgroundImage || backgroundImage === 'none') return false;

        const colors = backgroundImage.match(/rgba?\([^)]*\)|#[\da-f]{3,8}\b/gi) ?? [];
        return colors.some((color) => {
            const rgba = this.getRgba(color) ?? this.getHexRgba(color);
            return !!rgba && rgba.alpha > 0.9 && this.getLuminance(rgba) > 0.8;
        });
    }

    private getRgba(color: string) {
        const matches = color.match(/^rgba?\(([^)]+)\)$/);
        if (!matches) return undefined;
        const [red, green, blue, alpha = 1] = matches[1].split(',').map((value) => Number(value.trim()));
        if ([red, green, blue, alpha].some((value) => !Number.isFinite(value))) return undefined;
        return { red, green, blue, alpha };
    }

    private getHexRgba(color: string) {
        const value = color.slice(1);
        if (![3, 4, 6, 8].includes(value.length)) return undefined;

        const normalized =
            value.length < 5
                ? value
                      .split('')
                      .map((character) => character + character)
                      .join('')
                : value;
        const red = Number.parseInt(normalized.slice(0, 2), 16);
        const green = Number.parseInt(normalized.slice(2, 4), 16);
        const blue = Number.parseInt(normalized.slice(4, 6), 16);
        const alpha = normalized.length === 8 ? Number.parseInt(normalized.slice(6, 8), 16) / 255 : 1;
        return { red, green, blue, alpha };
    }

    private getLuminance({ red, green, blue }: { red: number; green: number; blue: number }) {
        return (red * 0.2126 + green * 0.7152 + blue * 0.0722) / 255;
    }

    private escapeHtml(value: string) {
        return value.replace(/[&<>"']/g, (character) => {
            const entities: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
            return entities[character];
        });
    }

    private applyCurrentThemeToFrames() {
        this.frameObservers.forEach((_observer, frame) => {
            const frameDocument = frame.contentDocument;
            if (frame.isConnected && frameDocument?.body) this.applyApplicationStyles(frame, frameDocument);
        });
    }

    ngOnInit(): void {
        this.themeObserver = new MutationObserver(() => this.applyCurrentThemeToFrames());
        this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }

    ngOnDestroy(): void {
        this.cancelReasoningAnimation();
        this.themeObserver?.disconnect();
        this.frameObservers.forEach((observer) => observer.disconnect());
        this.frameObservers.clear();
    }
}
